import { useCallback, useEffect, useRef, useState } from "react";

interface UseCopyToClipboardOptions {
  /** How long the `copied` flag stays true before resetting (ms). */
  resetMs?: number;
  /** Called after a successful copy — e.g. to fire a toast. */
  onCopy?: (text: string) => void;
}

/**
 * Tracks the "copied" checkmark-swap state shared by every copy button in the
 * app (invite links, audit log rows, tenant/role IDs, event payloads): copy to
 * the clipboard, flip a boolean for `resetMs`, then flip back.
 */
export function useCopyToClipboard({ resetMs = 2000, onCopy }: UseCopyToClipboardOptions = {}) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const copy = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      return false;
    }
    onCopy?.(text);
    setCopied(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setCopied(false), resetMs);
    return true;
  }, [onCopy, resetMs]);

  return { copied, copy };
}
